import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { GoogleLogin } from '@react-oauth/google';
import { deleteAccount } from '../api/auth';
import PasswordInput from './PasswordInput';

// hasUsablePassword comes from /api/users/me/. It decides which proof the
// server will accept: an account created through Google has no usable password,
// so check_password() there is False for every input and asking for one would
// be a dead end. Defaults to the password path -- while the profile is still
// loading the flag is undefined, and guessing "Google" there would strand every
// password user.
export default function DeleteAccountModal({
    open,
    onClose,
    onDeleted,
    hasUsablePassword = true,
}) {
    const usesPassword = hasUsablePassword !== false;
    const [confirmText, setConfirmText] = useState('');
    const [password, setPassword] = useState('');
    const [credential, setCredential] = useState('');
    const [error, setError] = useState('');

    const mutation = useMutation({
        mutationFn: deleteAccount,
        onSuccess: () => onDeleted(),
        onError: (err) => {
            const status = err?.response?.status;
            if (status === 403) {
                setError('Staff accounts cannot be deleted here. Contact an administrator.');
            } else if (status === 400) {
                setError(
                    usesPassword
                        ? 'Incorrect password. Please try again.'
                        : 'Could not verify that Google account. Please try again.',
                );
            } else {
                setError('Something went wrong. Please try again.');
            }
        },
    });

    // Lock background scroll while the modal is open, and close on Escape.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    // Reset fields whenever the modal is (re)opened.
    useEffect(() => {
        if (open) {
            setConfirmText('');
            setPassword('');
            setCredential('');
            setError('');
        }
    }, [open]);

    if (!open) return null;

    // The Google button only gathers proof; deleting stays behind the red
    // button either way. Wiring the mutation to the Google click would put a
    // destructive, irreversible action one stray click away.
    const proof = usesPassword ? password : credential;
    const canDelete =
        confirmText === 'DELETE' && proof.length > 0 && !mutation.isPending;

    const handleGoogleSuccess = (response) => {
        if (!response?.credential) {
            setError('Google sign-in was cancelled or blocked. Please try again.');
            return;
        }
        setError('');
        setCredential(response.credential);
    };

    const handleGoogleError = () => {
        setError('Google sign-in was cancelled or blocked. Please try again.');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canDelete) return;
        setError('');
        mutation.mutate(usesPassword ? { password } : { credential });
    };

    return (
        <div
            className="delete-modal-overlay"
            role="presentation"
            onClick={onClose}
        >
            <div
                className="delete-modal-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="delete-modal-header">
                    <h2 className="delete-modal-title" id="delete-modal-title">
                        Delete Account — are you sure?
                    </h2>
                    <button
                        type="button"
                        className="delete-modal-close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <ul className="delete-modal-list">
                    <li>Your account is deactivated immediately and you are logged out.</li>
                    <li>After a 30-day grace period, your account is permanently deleted.</li>
                    <li>
                        During the grace period you cannot re-register with the same
                        email or username.
                    </li>
                    <li>
                        Your volunteer, internship, and attendance records are kept as
                        organizational records.
                    </li>
                </ul>

                <form className="delete-modal-form" onSubmit={handleSubmit}>
                    <label className="delete-modal-label" htmlFor="delete-confirm">
                        Type <span className="delete-modal-accent">DELETE</span> to confirm
                    </label>
                    <input
                        id="delete-confirm"
                        type="text"
                        className="delete-modal-input"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        autoComplete="off"
                    />

                    {usesPassword ? (
                        <>
                            <label
                                className="delete-modal-label"
                                htmlFor="delete-password"
                            >
                                Enter your password
                            </label>
                            <PasswordInput
                                id="delete-password"
                                className="delete-modal-input"
                                wrapperClassName="delete-modal-field"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                            />
                        </>
                    ) : (
                        <>
                            <span className="delete-modal-label">
                                Confirm with your Google account
                            </span>
                            {credential ? (
                                <p className="delete-modal-verified" role="status">
                                    Google account confirmed.
                                </p>
                            ) : (
                                <div className="delete-modal-google">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {error && <p className="delete-modal-error">{error}</p>}

                    <div className="delete-modal-actions">
                        <button
                            type="button"
                            className="delete-modal-cancel"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="delete-modal-confirm"
                            disabled={!canDelete}
                        >
                            {mutation.isPending ? 'Deleting…' : 'Delete My Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
